import React from "react";
import { daysInMonth } from "../../utils/attendanceMonth";
import { printSymbol, tallyRow } from "../../utils/musterTally";

/**
 * The muster as a sheet to sign on paper.
 *
 * Same purpose as the CSV: employees who never touch the portal file nothing,
 * so every day of their register is the employer's assertion alone. A signed
 * sheet is the substitute for the agreement that never happened online. The CSV
 * gets you there via a spreadsheet; this is the version you can put in front of
 * a crew without formatting anything first.
 *
 * Deliberately plain and monochrome. A muster gets photocopied, faxed and filed,
 * so nothing may depend on colour — the statuses are already letters, and the
 * only emphasis is weight and rules.
 */

const WEEKDAY_INITIAL = ["S", "M", "T", "W", "T", "F", "S"];

const MusterPrintSheet = ({ rows = [], year, month, monthName, scopeLabel }) => {
  const total = daysInMonth(year, month);
  const days = Array.from({ length: total }, (_, i) => {
    const d = i + 1;
    const weekday = WEEKDAY_INITIAL[new Date(Date.UTC(year, month, d)).getUTCDay()];
    return { d, weekday };
  });

  return (
    <div className="muster-print">
      <style>{`
        .muster-print { display: none; }
        @media print {
          /* the grid is 31 day columns wide before names and signatures */
          @page { size: A4 landscape; margin: 10mm; }

          /* everything except the sheet: the app chrome has no business on paper */
          body * { visibility: hidden !important; }
          .muster-print, .muster-print * { visibility: visible !important; }
          .muster-print {
            display: block !important;
            position: absolute; left: 0; top: 0; width: 100%;
            color: #000; background: #fff;
            font-family: -apple-system, Segoe UI, Roboto, sans-serif;
          }
          .muster-print h1 { font-size: 13pt; margin: 0 0 2mm; }
          .muster-print .meta { font-size: 8pt; margin-bottom: 2mm; }
          .muster-print .legend { font-size: 7.5pt; margin-bottom: 3mm; }
          .muster-print table { width: 100%; border-collapse: collapse; font-size: 6.5pt; }
          .muster-print th, .muster-print td {
            border: 0.4pt solid #000; padding: 0.6mm 0.4mm; text-align: center;
          }
          .muster-print th.name, .muster-print td.name {
            text-align: left; width: 34mm; padding-left: 1mm; font-size: 7pt;
          }
          .muster-print td.name .id { font-size: 5.5pt; color: #444; }
          .muster-print th.sig, .muster-print td.sig { width: 32mm; }
          .muster-print th.tot, .muster-print td.tot { width: 6mm; }
          /* the header repeats on every sheet, or page two is unreadable */
          .muster-print thead { display: table-header-group; }
          .muster-print tr { break-inside: avoid; page-break-inside: avoid; }
          .muster-print .weekend { background: #eee; }
          .muster-print .signoff { margin-top: 5mm; font-size: 8pt; }
          .muster-print .signoff .line {
            display: inline-block; border-bottom: 0.4pt solid #000;
            width: 55mm; margin: 0 6mm 0 2mm;
          }
        }
      `}</style>

      <h1>
        Attendance muster — {monthName} {year}
        {scopeLabel ? ` · ${scopeLabel}` : ""}
      </h1>
      <div className="meta">
        {rows.length} employee{rows.length === 1 ? "" : "s"} · generated {new Date().toLocaleString()}
      </div>
      {/*
        Whoever signs will not have the application open beside them.

        On screen presence is a dot; here it is a P, because a tint is the only
        thing carrying meaning in the grid and a tint does not survive a
        black-and-white photocopier. "L Late" used to sit in this legend beside
        the leave codes, which put the same letter against two different
        meanings on a document people sign — a late arrival is a day the
        employee was present, and the minutes belong in the register.
      */}
      <div className="legend">
        P Present · A Absent · MP Missing punch · L / CL / SL Leave (leave type's code) ·
        WO Weekly off · H Holiday · blank Not yet
        {" · "}<strong>*</strong> marks a day whose punch was corrected rather than captured by the device
        {" · "}<strong>P/WD</strong> is days present against days rostered — a missing punch counts against
        neither presence nor absence until it is regularised
      </div>

      <table>
        <thead>
          <tr>
            <th className="name">Employee</th>
            {days.map((d) => (
              <th key={d.d} className={d.weekday === "S" ? "weekend" : ""}>
                <div>{d.weekday}</div>
                <div>{d.d}</div>
              </th>
            ))}
            <th className="tot">P</th>
            <th className="tot">WD</th>
            <th className="tot">A</th>
            <th className="tot">L</th>
            <th className="sig">Signature</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // the same tally the screen shows, from the same module, so the
            // sheet somebody signs cannot disagree with the grid they read it on
            const t = tallyRow(r.days);
            return (
              <tr key={r.id || r._id}>
                <td className="name">
                  <div>{r.name}</div>
                  <div className="id">{r.aliasId || ""}</div>
                </td>
                {r.days.map((d) => (
                  <td key={d.dayString} className={d.isWeeklyOff ? "weekend" : ""}>
                    {printSymbol(d)}
                  </td>
                ))}
                <td className="tot">{t.present}</td>
                <td className="tot">{t.workingDays}</td>
                <td className="tot">{t.absent}</td>
                <td className="tot">{t.leave}</td>
                <td className="sig" />
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Named, because an assertion nobody signed for is not evidence. The
          countersignature is what makes this a two-party record rather than the
          employer's own word. */}
      <div className="signoff">
        <div style={{ marginBottom: "3mm" }}>
          Corrected days are marked <strong>*</strong>. Signing confirms these hours are accurate.
        </div>
        Supervisor<span className="line" />Signature<span className="line" />Date<span className="line" />
      </div>
    </div>
  );
};

export default MusterPrintSheet;
